/**
 * Athlete Nutrition Dashboard — Phase 2.1B
 *
 * Bound to: GET /api/nutrition-analytics/athlete/{athlete_id}
 *
 * Scrollable dashboard with sections:
 *   1. Identity + active target hero
 *   2. Anthropometry timeline (line chart)
 *   3. Body composition timeline (line chart + latest method)
 *   4. Current meal plan vs target deltas
 *   5. Integration callouts (wellness / load / RTP)
 *
 * Role-aware: NUTRITIONIST+ADMIN see edit CTAs; others see read-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Apple, Heart, Activity, ShieldCheck, AlertTriangle,
  Pencil, Plus, UserCircle2,
} from 'lucide-react';
import {
  Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import NutritionProfileEditorDialog from '../../components/nutrition/NutritionProfileEditorDialog';
import AnthropometryEditorDialog from '../../components/nutrition/AnthropometryEditorDialog';
import AnthropometryHistoryTable from '../../components/nutrition/AnthropometryHistoryTable';
import BodyCompositionEditorDialog from '../../components/nutrition/BodyCompositionEditorDialog';
import BodyCompositionHistoryTable from '../../components/nutrition/BodyCompositionHistoryTable';
import BodyCompositionTimeline from '../../components/nutrition/BodyCompositionTimeline';
import BodyCompositionSummaryCard from '../../components/nutrition/BodyCompositionSummaryCard';
import TargetWorkspaceDialog from '../../components/nutrition/TargetWorkspaceDialog';
import TargetSummaryCard from '../../components/nutrition/TargetSummaryCard';
import TargetHistoryTable from '../../components/nutrition/TargetHistoryTable';
import MealPlanEditorDialog from '../../components/nutrition/MealPlanEditorDialog';
import MealPlanSummaryCard from '../../components/nutrition/MealPlanSummaryCard';
import MealPlanViewer from '../../components/nutrition/MealPlanViewer';
import MealPlanVersionHistory from '../../components/nutrition/MealPlanVersionHistory';

const fmt = (v, dp = 1, suffix = '') =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(dp)}${suffix}`;
const fmtInt = (v, suffix = '') =>
  v === null || v === undefined ? '—' : `${Math.round(v)}${suffix}`;

const StatCard = ({ label, value, hint, accent = 'primary', testid }) => (
  <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
    <div className={`text-2xl font-bold ${
      accent === 'good' ? 'text-emerald-400' :
      accent === 'warn' ? 'text-amber-400' :
      accent === 'critical' ? 'text-red-400' :
      'text-cyan-300'
    }`}>{value}</div>
    {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
  </Card>
);

const ProfileChips = ({ label, values, colorClass, testid }) => {
  const items = Array.isArray(values) ? values : [];
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      {items.length === 0 ? (
        <span className="text-zinc-600 text-sm italic">—</span>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((v, i) => (
            <Badge key={i} className={`${colorClass} text-xs`}>{v}</Badge>
          ))}
        </div>
      )}
    </div>
  );
};

const AthleteNutritionDashboardPage = () => {
  const { athleteId } = useParams();
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);
  const [anthroEditorOpen, setAnthroEditorOpen] = useState(false);
  const [anthroRefreshKey, setAnthroRefreshKey] = useState(0);
  const [bodyCompEditorOpen, setBodyCompEditorOpen] = useState(false);
  const [bodyCompRows, setBodyCompRows] = useState([]);
  const [bodyCompRefreshKey, setBodyCompRefreshKey] = useState(0);
  const [targetEditorOpen, setTargetEditorOpen] = useState(false);
  const [targetRefreshKey, setTargetRefreshKey] = useState(0);
  const [mealPlanEditorOpen, setMealPlanEditorOpen] = useState(false);
  const [mealPlanDoc, setMealPlanDoc] = useState({ plan: null, current_version: null, versions: [] });
  const [mealPlanRefreshKey, setMealPlanRefreshKey] = useState(0);

  const canWrite = ['ADMIN', 'NUTRITIONIST', 'SUPERADMIN'].includes(user?.role);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/nutrition-analytics/athlete/${athleteId}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [api, athleteId]);

  const loadBodyComp = useCallback(async () => {
    try {
      const res = await api.get(`/nutrition/body-composition/${athleteId}`);
      setBodyCompRows(res.data || []);
    } catch {
      setBodyCompRows([]);
    }
  }, [api, athleteId]);

  const loadMealPlan = useCallback(async () => {
    try {
      const res = await api.get(`/nutrition/meal-plans/${athleteId}`);
      setMealPlanDoc(res.data || { plan: null, current_version: null, versions: [] });
    } catch {
      setMealPlanDoc({ plan: null, current_version: null, versions: [] });
    }
  }, [api, athleteId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadBodyComp(); }, [loadBodyComp, bodyCompRefreshKey]);
  useEffect(() => { loadMealPlan(); }, [loadMealPlan, mealPlanRefreshKey]);

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        {language === 'IT' ? 'Caricamento…' : 'Loading…'}
      </div>
    );
  }

  if (error || !data || data.error) {
    return (
      <div className="p-12 text-center text-zinc-400" data-testid="athlete-nutrition-error">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
        <p>{error || (language === 'IT' ? 'Atleta non trovato' : 'Athlete not found')}</p>
      </div>
    );
  }

  const ath = data.athlete;
  const target = data.active_target;
  const anthroLatest = data.anthropometry?.latest;
  const anthroTimeline = (data.anthropometry?.timeline || []).map((r) => ({
    date: r.date,
    weight: r.weight_kg,
    bf: r.body_fat_pct,
    lean: r.lean_mass_kg,
    bmr: r.bmr_kcal,
    tdee: r.tdee_kcal,
  }));
  const callouts = data.callouts || {};

  const weightProgress = data.anthropometry?.progress?.weight_kg || {};
  const bfProgress = data.anthropometry?.progress?.body_fat_pct || {};

  return (
    <div className="space-y-6 p-6" data-testid="athlete-nutrition-dashboard">
      {/* Header */}
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/staff/nutrition')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {language === 'IT' ? 'Indietro' : 'Back'}
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Apple className="w-6 h-6 text-emerald-400" />
              <h1 className="text-3xl font-bold text-zinc-100">
                {ath.first_name} {ath.last_name}
              </h1>
              {ath.jersey_number !== null && (
                <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                  #{ath.jersey_number}
                </Badge>
              )}
              {ath.status === 'RTP' && (
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">RTP</Badge>
              )}
            </div>
            <p className="text-sm text-zinc-400">
              {ath.primary_position || '—'} ·{' '}
              {language === 'IT' ? 'Altezza' : 'Height'}: {fmt(ath.height_cm, 0, ' cm')} ·{' '}
              {language === 'IT' ? 'Sesso' : 'Sex'}: {ath.sex || '—'}
            </p>
          </div>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2" data-testid="nutrition-actions-bar">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setProfileEditorOpen(true)}
              data-testid="open-profile-editor"
              className="border-zinc-700 hover:border-cyan-600 hover:text-cyan-300"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              {language === 'IT' ? 'Modifica profilo' : 'Edit profile'}
            </Button>
            <Button
              size="sm"
              onClick={() => setAnthroEditorOpen(true)}
              data-testid="open-anthro-editor"
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              {language === 'IT' ? 'Nuova misurazione' : 'Add measurement'}
            </Button>
            <Button
              size="sm"
              onClick={() => setBodyCompEditorOpen(true)}
              data-testid="open-bodycomp-editor"
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              {language === 'IT' ? 'Nuova body comp.' : 'Add body comp.'}
            </Button>
            <Button
              size="sm"
              onClick={() => setTargetEditorOpen(true)}
              data-testid="open-target-editor"
              className="bg-violet-600 hover:bg-violet-500 text-white"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              {language === 'IT' ? 'Nuovo target' : 'New target'}
            </Button>
            <Button
              size="sm"
              onClick={() => setMealPlanEditorOpen(true)}
              data-testid="open-mealplan-editor"
              className="bg-amber-600 hover:bg-amber-500 text-white"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              {language === 'IT' ? 'Nuovo piano' : 'New plan'}
            </Button>
          </div>
        )}
      </header>

      {/* Active target hero */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          testid="kpi-weight"
          label={language === 'IT' ? 'Peso attuale' : 'Latest weight'}
          value={fmt(anthroLatest?.weight_kg, 1, ' kg')}
          hint={weightProgress.delta_pct !== null && weightProgress.delta_pct !== undefined ? `Δ ${weightProgress.delta_pct >= 0 ? '+' : ''}${weightProgress.delta_pct.toFixed(1)}%` : null}
        />
        <StatCard
          testid="kpi-bf"
          label="BF %"
          value={fmt(anthroLatest?.body_fat_pct, 1, ' %')}
          hint={bfProgress.delta_pct !== null && bfProgress.delta_pct !== undefined ? `Δ ${bfProgress.delta_pct >= 0 ? '+' : ''}${bfProgress.delta_pct.toFixed(1)}%` : null}
        />
        <StatCard
          testid="kpi-lean"
          label={language === 'IT' ? 'Massa magra' : 'Lean mass'}
          value={fmt(anthroLatest?.lean_mass_kg, 1, ' kg')}
        />
        <StatCard
          testid="kpi-bmr"
          label="BMR"
          value={fmtInt(anthroLatest?.bmr_kcal, ' kcal')}
          hint={anthroLatest?.bmr_formula}
        />
        <StatCard
          testid="kpi-tdee"
          label="TDEE"
          value={fmtInt(anthroLatest?.tdee_kcal, ' kcal')}
          hint={anthroLatest?.activity_level}
        />
      </div>

      {/* Active target — Phase 2.2B Session 3 */}
      <TargetSummaryCard
        target={target}
        weightKg={anthroLatest?.weight_kg}
        canEdit={canWrite}
        onEdit={() => setTargetEditorOpen(true)}
      />
      <TargetHistoryTable athleteId={athleteId} refreshKey={targetRefreshKey} />

      {/* Nutrition Profile summary — Phase 2.2B */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="profile-summary-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UserCircle2 className="w-5 h-5 text-zinc-400" />
            <h2 className="text-lg font-semibold text-zinc-100">
              {language === 'IT' ? 'Profilo nutrizionale' : 'Nutrition profile'}
            </h2>
          </div>
          {canWrite && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setProfileEditorOpen(true)}
              data-testid="profile-summary-edit"
              className="text-zinc-400 hover:text-cyan-300"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              {language === 'IT' ? 'Modifica' : 'Edit'}
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <ProfileChips
            label={language === 'IT' ? 'Allergie' : 'Allergies'}
            values={data.profile?.allergies}
            colorClass="bg-red-500/20 text-red-300 border-red-500/40"
            testid="summary-allergies"
          />
          <ProfileChips
            label={language === 'IT' ? 'Intolleranze' : 'Intolerances'}
            values={data.profile?.intolerances}
            colorClass="bg-amber-500/20 text-amber-300 border-amber-500/40"
            testid="summary-intolerances"
          />
          <ProfileChips
            label={language === 'IT' ? 'Preferenze alimentari' : 'Dietary preferences'}
            values={data.profile?.dietary_preferences}
            colorClass="bg-zinc-700/60 text-zinc-200 border-zinc-600"
            testid="summary-preferences"
          />
          <ProfileChips
            label={language === 'IT' ? 'Condizioni mediche' : 'Medical flags'}
            values={data.profile?.medical_flags}
            colorClass="bg-purple-500/20 text-purple-300 border-purple-500/40"
            testid="summary-medical-flags"
          />
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              {language === 'IT' ? 'Note' : 'Notes'}
            </div>
            <div
              className="text-zinc-300 text-sm whitespace-pre-wrap min-h-[1.5rem]"
              data-testid="summary-notes"
            >
              {data.profile?.nutrition_notes || <span className="text-zinc-600 italic">—</span>}
            </div>
          </div>
        </div>
      </Card>

      {/* Anthropometry timeline */}
      {anthroTimeline.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="anthro-chart">
          <h2 className="text-lg font-semibold text-zinc-100 mb-3">
            {language === 'IT' ? 'Andamento Antropometria' : 'Anthropometry Trend'}
          </h2>
          <div className="h-72 min-h-[18rem]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={anthroTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" stroke="#06b6d4" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="weight" name={language === 'IT' ? 'Peso (kg)' : 'Weight (kg)'} stroke="#06b6d4" strokeWidth={2} dot={{ r: 4 }} />
                <Line yAxisId="right" type="monotone" dataKey="bf" name="BF %" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                <Line yAxisId="left" type="monotone" dataKey="lean" name={language === 'IT' ? 'Magra (kg)' : 'Lean (kg)'} stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Anthropometry history (append-only table) — Phase 2.2B */}
      <AnthropometryHistoryTable athleteId={athleteId} refreshKey={anthroRefreshKey} />

      {/* Body composition — Phase 2.2B Session 2 */}
      <BodyCompositionSummaryCard rows={bodyCompRows} />
      <BodyCompositionTimeline rows={bodyCompRows} />
      <BodyCompositionHistoryTable athleteId={athleteId} refreshKey={bodyCompRefreshKey} />

      {/* Meal Plan workspace — Phase 2.2B Session 4 */}
      <MealPlanSummaryCard
        version={mealPlanDoc.current_version}
        plan={mealPlanDoc.plan}
        canEdit={canWrite}
        onNewVersion={() => setMealPlanEditorOpen(true)}
      />
      {mealPlanDoc.current_version && (
        <MealPlanViewer version={mealPlanDoc.current_version} />
      )}
      <MealPlanVersionHistory
        athleteId={athleteId}
        refreshKey={mealPlanRefreshKey}
        canRestore={canWrite}
        onRestored={() => { setMealPlanRefreshKey((k) => k + 1); loadData(); }}
      />

      {/* Integration callouts — Phase 2.1D */}
      {callouts.rtp_timeline && (
        <Card className="bg-amber-500/5 border-amber-500/40 p-4" data-testid="rtp-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-amber-400" />
              <div>
                <div className="text-amber-300 font-semibold text-sm uppercase tracking-wider">
                  {language === 'IT' ? 'RTP attivo' : 'Active RTP'}
                </div>
                <div className="text-zinc-200 text-sm mt-0.5">
                  {callouts.rtp_timeline.injury_type} ·{' '}
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 ml-1">
                    {callouts.rtp_timeline.current_phase}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="text-xs text-zinc-400">
              {language === 'IT' ? 'Iniziato' : 'Started'}: {callouts.rtp_timeline.start_date} ·{' '}
              {language === 'IT' ? 'Ritorno previsto' : 'Expected return'}:{' '}
              {callouts.rtp_timeline.expected_return_date || '—'}
            </div>
          </div>
          {target?.goal === 'RTP' && (
            <div className="mt-3 pt-3 border-t border-amber-500/20 text-xs text-amber-200">
              {language === 'IT'
                ? '⚡ Obiettivo nutrizionale allineato al protocollo RTP'
                : '⚡ Nutrition goal aligned with RTP protocol'}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="integration-callouts">
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="wellness-widget">
          <div className="flex items-center gap-2 mb-2 text-zinc-400 text-xs uppercase">
            <Heart className="w-3.5 h-3.5" />
            {language === 'IT' ? 'Wellness (7gg)' : 'Wellness (7d)'}
          </div>
          {callouts.wellness_window ? (
            <div className="space-y-1 text-sm">
              <div className="text-zinc-300">
                {language === 'IT' ? 'Score medio' : 'Avg score'}:{' '}
                <span className="font-medium text-cyan-300">
                  {callouts.wellness_window.avg_readiness ?? '—'}
                </span>
                {callouts.wellness_latest?.alert_level && (
                  <Badge className={`ml-2 ${
                    callouts.wellness_latest.alert_level === 'RED' ? 'bg-red-500/20 text-red-300 border-red-500/40' :
                    callouts.wellness_latest.alert_level === 'AMBER' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
                    'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                  }`}>{callouts.wellness_latest.alert_level}</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-1 text-[11px] text-zinc-400">
                <div>{language === 'IT' ? 'Sonno' : 'Sleep'}: {callouts.wellness_window.avg_sleep ?? '—'}</div>
                <div>{language === 'IT' ? 'Fatica' : 'Fatigue'}: {callouts.wellness_window.avg_fatigue ?? '—'}</div>
                <div>{language === 'IT' ? 'DOMS' : 'Soreness'}: {callouts.wellness_window.avg_soreness ?? '—'}</div>
                <div>{language === 'IT' ? 'Stress' : 'Stress'}: {callouts.wellness_window.avg_stress ?? '—'}</div>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">{callouts.wellness_window.samples} {language === 'IT' ? 'misurazioni' : 'samples'}</div>
            </div>
          ) : <span className="text-zinc-500 text-sm">—</span>}
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="load-widget">
          <div className="flex items-center gap-2 mb-2 text-zinc-400 text-xs uppercase">
            <Activity className="w-3.5 h-3.5" />
            {language === 'IT' ? 'Carico (GPS)' : 'Load (GPS)'}
          </div>
          {callouts.load && (callouts.load.acute_load !== null || callouts.load.chronic_load !== null) ? (
            <div className="space-y-1 text-sm">
              <div className="grid grid-cols-2 gap-1 text-[11px]">
                <div className="text-zinc-400">{language === 'IT' ? 'Acuto (7gg)' : 'Acute (7d)'}:</div>
                <div className="text-zinc-200 font-medium">{callouts.load.acute_load ?? '—'} AU</div>
                <div className="text-zinc-400">{language === 'IT' ? 'Cronico (28gg)' : 'Chronic (28d)'}:</div>
                <div className="text-zinc-200 font-medium">{callouts.load.chronic_load ?? '—'} AU</div>
                <div className="text-zinc-400">{language === 'IT' ? 'Settimanale' : 'Weekly'}:</div>
                <div className="text-zinc-200 font-medium">{callouts.load.weekly_load ?? '—'} AU</div>
                <div className="text-zinc-400">{language === 'IT' ? 'Mensile' : 'Monthly'}:</div>
                <div className="text-zinc-200 font-medium">{callouts.load.monthly_load ?? '—'} AU</div>
                <div className="text-zinc-400">ACWR:</div>
                <div className={`font-medium ${
                  callouts.load.acwr !== null && callouts.load.acwr > 1.5 ? 'text-red-300' :
                  callouts.load.acwr !== null && callouts.load.acwr < 0.8 ? 'text-amber-300' :
                  'text-emerald-300'
                }`}>{callouts.load.acwr ?? '—'}</div>
              </div>
            </div>
          ) : (
            <span className="text-zinc-500 text-sm">
              {language === 'IT' ? 'Nessun dato GPS recente' : 'No recent GPS data'}
            </span>
          )}
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-4">
          <div className="flex items-center gap-2 mb-2 text-zinc-400 text-xs uppercase">
            <ShieldCheck className="w-3.5 h-3.5" />
            RTP
          </div>
          <div className="text-zinc-200 text-sm">
            {callouts.rtp_active ? (
              <>
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">{callouts.rtp_active.phase}</Badge>{' '}
                <span className="text-xs text-zinc-400">{callouts.rtp_active.injury_type}</span>
              </>
            ) : <span className="text-zinc-500">{language === 'IT' ? 'Nessun RTP attivo' : 'No active RTP'}</span>}
          </div>
        </Card>
      </div>

      {/* Editor dialogs — Phase 2.2B */}
      {canWrite && (
        <>
          <NutritionProfileEditorDialog
            open={profileEditorOpen}
            onClose={() => setProfileEditorOpen(false)}
            athleteId={athleteId}
            athleteName={`${ath.first_name} ${ath.last_name}`}
            initial={data.profile}
            onSaved={() => { loadData(); }}
          />
          <AnthropometryEditorDialog
            open={anthroEditorOpen}
            onClose={() => setAnthroEditorOpen(false)}
            athleteId={athleteId}
            athleteName={`${ath.first_name} ${ath.last_name}`}
            heightHint={ath.height_cm}
            onSaved={() => { loadData(); setAnthroRefreshKey((k) => k + 1); }}
          />
          <BodyCompositionEditorDialog
            open={bodyCompEditorOpen}
            onClose={() => setBodyCompEditorOpen(false)}
            athleteId={athleteId}
            athleteName={`${ath.first_name} ${ath.last_name}`}
            defaults={{
              sex: ath.sex || 'M',
              weight_kg: anthroLatest?.weight_kg ?? '',
              age_years: ath.birth_date
                ? Math.floor((Date.now() - new Date(ath.birth_date).getTime()) / (365.25 * 86400000))
                : '',
            }}
            onSaved={() => { setBodyCompRefreshKey((k) => k + 1); loadData(); }}
          />
          <TargetWorkspaceDialog
            open={targetEditorOpen}
            onClose={() => setTargetEditorOpen(false)}
            athleteId={athleteId}
            athlete={ath}
            latestAnthro={anthroLatest}
            onSaved={() => { setTargetRefreshKey((k) => k + 1); loadData(); }}
          />
          <MealPlanEditorDialog
            open={mealPlanEditorOpen}
            onClose={() => setMealPlanEditorOpen(false)}
            athleteId={athleteId}
            athleteName={`${ath.first_name} ${ath.last_name}`}
            activeTarget={target}
            seedFromVersion={mealPlanDoc.current_version}
            onSaved={() => { setMealPlanRefreshKey((k) => k + 1); loadData(); }}
          />
        </>
      )}
    </div>
  );
};

export default AthleteNutritionDashboardPage;
